import { ImageResource, LanguageResource, TranslationEntry } from "@dgs/core";
import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { Currency } from "../../types/currency";
import {
	EventCostCategoryResource,
	EventFormTranslationValues,
	EventFormValues,
	EventVisibility,
} from "../../types/event";
import { EventTypeOptionResource } from "../../types/eventType";
import { LocationOptionResource } from "../../types/location";

export interface CreateEventTranslationValue {
	name: string;
}

export enum SimilarEvents {
	NONE = "NONE",
	PENDING = "PENDING",
	IGNORED = "IGNORED",
	TRUE = "TRUE",
}

export interface CreateEventInitialValues {
	translations: TranslationEntry<CreateEventTranslationValue>[];
	timezone: string;
	start: string | null;
	end: string | null;
	eventType: EventTypeOptionResource | null;
	location: LocationOptionResource | null;
	primaryLanguage: LanguageResource | null;
	image: ImageResource | null;
	hasSimilarEvents: SimilarEvents;
	visibility: EventVisibility;
}

interface CreateEventState {
	initialValues: EventFormValues | null;
	setDrawerValues: (newValues: CreateEventInitialValues | null) => void;
	mergeFormValues: (values: CreateEventInitialValues) => EventFormValues;
}

const CreateEventContext = createContext<CreateEventState | undefined>(undefined);

interface Props {
	defaultCurrency: Currency;
	children: ReactNode;
}

export function CreateEventProvider({ children, defaultCurrency }: Props) {
	const [drawerValues, setDrawerValues] = useState<CreateEventInitialValues | null>(null);

	const mergeFormValues = useCallback(
		(values: CreateEventInitialValues) => {
			const translations: TranslationEntry<EventFormTranslationValues>[] = values.translations.map((translation) => ({
				locale: translation.locale,
				name: translation.name,
				additionalInfo: [{ type: "paragraph", children: [{ text: "" }] }],
			}));

			const costCategories: EventCostCategoryResource[] | undefined = values.eventType?.costCategoryRequirements
				.filter((costCategoryRequirement) => costCategoryRequirement.requirement !== "prohibited")
				.map((costCategoryRequirement) => ({
					costCategory: costCategoryRequirement.costCategory,
					plannedCost: null,
					actualCost: null,
					comment: null,
					commentAllowed: false,
				}));

			return {
				// Step 1
				...values,
				translations: translations,
				languages: [],
				createdByUser: { id: "-1", name: "test", email: "test" },
				organizer: null,
				// Step 1.5
				address: {
					street: "",
					houseNumber: "",
					zip: "",
					city: "",
					country: null,
					additionalInfo: "",
				},
				// Step 2
				eventUsers: {},
				// Step 3
				isInviteOnly: null,
				link: "",
				tenantPlannedPersonCounts: [],
				// Step 4
				currency: defaultCurrency,
				eventCostCategories: costCategories ?? [],
				// Step 5
				format: null,
				eventCategory: null,
				industries: [],
				targetGroup: null,
				region: null,
				primaryOrganizationalUnit: null,
				organizationalUnits: [],
				tags: [],
				preferences: [],
				isHighlight: false,
				eventGoal: null,
			};
		},
		[defaultCurrency],
	);

	const initialValues: EventFormValues | null = useMemo(() => {
		if (drawerValues === null) {
			return null;
		}

		return mergeFormValues(drawerValues);
	}, [drawerValues, mergeFormValues]);

	return (
		<CreateEventContext.Provider value={{ initialValues, setDrawerValues, mergeFormValues }}>
			{children}
		</CreateEventContext.Provider>
	);
}

export function useCreateEvent() {
	const ctx = useContext(CreateEventContext);

	if (ctx === undefined) {
		throw new Error("useCreateEvent needs to be wrapped inside a CreateEventProvider");
	}

	return ctx;
}
